import React, { PropsWithChildren, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuthContext } from "../auth/AuthProvider";
import { useCompanyContext } from "../components/companies/useCompanyContext";
import { withReactErrorBoundary } from "../components/utility/ReactErrorBoundary";
import { analyticsClient } from "./client";
import { EVENTS } from "./events";
import { resolvePageName } from "./pageName";
import { buildSuperProperties, resolvePrimaryAccountType } from "./superProperties";

const APP_VERSION = typeof process !== "undefined" ? process.env.APP_VERSION || "unknown" : "unknown";

/** Belt-and-braces around effect bodies: `analyticsClient` already swallows SDK errors, but the
 * surrounding wiring (super-property builders, context reads) is untrusted from an analytics
 * standpoint — any throw here must never break the host page. */
function safeEffect(label: string, fn: () => void): void {
  try {
    fn();
  } catch (error) {
    console.error(`Analytics effect '${label}' failed:`, error);
  }
}

/**
 * Cross-cutting analytics wiring: initialises the SDK, keeps super-properties and the
 * non-PII people profile in sync with the active user/company/role/feature context
 * (FR-002/FR-003/FR-004), and emits `page_viewed` on route change (FR-012).
 * Mounted inside BrowserRouter + CompanyContextProvider (see App.tsx).
 */
const AnalyticsProviderInner: React.FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const { currentUser } = useAuthContext();
  const { companyId, userCompanyRoles, features } = useCompanyContext();
  const lastTrackedPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    safeEffect("init", () => analyticsClient.init());
  }, []);

  useEffect(() => {
    safeEffect("register-super-properties", () => {
      const superProperties = buildSuperProperties(currentUser, companyId, userCompanyRoles, features, APP_VERSION);
      analyticsClient.register(superProperties as unknown as Record<string, unknown>);

      if (currentUser) {
        analyticsClient.setProfile({
          internal_user: superProperties.internal_user,
          primary_account_type: resolvePrimaryAccountType(currentUser),
        });
      }
    });
  }, [currentUser, companyId, userCompanyRoles, features]);

  useEffect(() => {
    if (lastTrackedPathnameRef.current === location.pathname) return;
    lastTrackedPathnameRef.current = location.pathname;
    safeEffect("page-viewed", () =>
      analyticsClient.track(EVENTS.PAGE_VIEWED, { page_name: resolvePageName(location.pathname) })
    );
  }, [location.pathname]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

/**
 * Analytics is strictly non-essential — a render-phase throw from Mixpanel or the surrounding
 * super-property wiring must never blank the app. If the inner provider fails, fall back to
 * rendering children directly so the rest of the tree keeps working with analytics silently
 * disabled.
 */
export const AnalyticsProvider = withReactErrorBoundary<PropsWithChildren>(AnalyticsProviderInner, {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  fallback: ({ children }) => <>{children}</>,
  onError: (error) => {
    console.error("AnalyticsProvider failed to render:", error);
  },
});
